import { useCallback, useEffect, useState } from 'react'
import { integrationsApi, integrationRecipientApi } from '@/api/integrations'
import {
  getIntegrationDisplayHandle,
  getNotificationIntegration,
  getNotificationIntegrationRefetchEvent,
  isActivationRequiredForRole,
  type NotificationIntegrationProvider,
  type NotificationIntegrationStatus,
} from '@/helpers/notificationIntegration'

interface UseNotificationIntegrationResult {
  status: NotificationIntegrationStatus
  integrationId: number | null
  displayHandle: string | null
  necessarily: boolean
}

export const useNotificationIntegration = (
  provider: NotificationIntegrationProvider,
  userId: string | number | null | undefined,
  userRole: string | null | undefined,
): UseNotificationIntegrationResult => {
  const [status, setStatus] = useState<NotificationIntegrationStatus>('hidden')
  const [integrationId, setIntegrationId] = useState<number | null>(null)
  const [displayHandle, setDisplayHandle] = useState<string | null>(null)
  const [necessarily, setNecessarily] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)

  const refetch = useCallback(() => setFetchKey((k) => k + 1), [])

  useEffect(() => {
    const eventName = getNotificationIntegrationRefetchEvent(provider)
    const handler = () => refetch()
    window.addEventListener(eventName, handler)
    return () => window.removeEventListener(eventName, handler)
  }, [provider, refetch])

  useEffect(() => {
    if (!userId || !userRole) {
      setStatus('hidden')
      setIntegrationId(null)
      setDisplayHandle(null)
      setNecessarily(false)
      return
    }

    integrationsApi
      .getAll(provider)
      .then((res: any) => {
        const items = res?.data?.data ?? res?.data ?? []
        const integration = getNotificationIntegration(items)

        if (!integration || integration.status !== 'connected') {
          setStatus('setup')
          setIntegrationId(null)
          setDisplayHandle(null)
          setNecessarily(false)
          return null
        }

        setNecessarily(isActivationRequiredForRole(userRole, integration))
        setIntegrationId(integration.id)
        setDisplayHandle(getIntegrationDisplayHandle(integration))

        return integrationRecipientApi
          .checkIsActive(integration.id, Number(userId))
          .then((bindingRes: any) => {
            const isActive =
              bindingRes?.data?.data?.is_active ??
              bindingRes?.data?.is_active ??
              false

            setStatus(isActive ? 'connected' : 'activate')
          })
      })
      .catch(() => {
        setStatus('setup')
        setIntegrationId(null)
        setDisplayHandle(null)
        setNecessarily(false)
      })
  }, [provider, userId, userRole, fetchKey])

  return { status, integrationId, displayHandle, necessarily }
}
